import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { useState, useEffect, useMemo } from 'react';
import { Flower2, SlidersHorizontal, ChevronDown } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { CategorySeoShowcase } from '@/components/CategorySeoShowcase';
import { KWIATY_SEO_CONFIG } from '@/config/categorySeoShowcase';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import {
  fetchAllCollectionProducts,
  fetchAllProducts,
  productDisplayPrice,
  type ShopifyProduct,
} from '@/lib/shopifyApi';
import { CollectionProductCard } from '@/components/collection/CollectionProductCard';
import {
  CollectionFilters,
  type FilterState,
  EMPTY_FILTERS,
  hasActiveFilters,
  OKAZJE,
  KWIATY,
} from '@/components/collection/CollectionFilters';

/* ─── Design tokens ────────────────────────────────────── */
const DARK   = '#2A1F14';
const GOLD   = '#e05590';
const PINK   = '#D75B76';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const SEP    = '#ECE3E1';

import { collectionUrl } from '@/lib/collectionUrl';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';


/* ─── Composition types (kwiaty-specific filter) ──────── */
const KWIATY_KOMPOZYCJE: { value: string; label: string; emoji: string; handle: string }[] = [
  { value: 'Bukiety',          label: 'Bukiety',          emoji: '💐', handle: 'bukiety' },
  { value: 'Flower Box',       label: 'Flower Box',       emoji: '🎁', handle: 'flowerbox' },
  { value: 'Kosze kwiatowe',   label: 'Kosze kwiatowe',   emoji: '🧺', handle: 'kosze-kwiatowe' },
  { value: 'Kwiaty w torebce', label: 'Kwiaty w torebce', emoji: '🛍️', handle: 'kwiaty-w-torebce' },
  { value: 'Kompozycje pogrzebowe', label: 'Kompozycje pogrzebowe', emoji: '🕯️', handle: 'kompozycje-pogrzebowe' },
];

const KWIATY_VISIBLE_SECTIONS = ['kompozycje', 'okazje', 'kwiaty', 'cena'] as const;

/* ─── Sort ────────────────────────────────────────────── */
const SORT_OPTIONS = [
  { label: 'Polecane',          value: 'default',    reverse: false },
  { label: 'Najpopularniejsze', value: 'best',        reverse: false },
  { label: 'Cena ↑',            value: 'price-asc',   reverse: false },
  { label: 'Cena ↓',            value: 'price-desc',  reverse: false },
  { label: 'Najnowsze',         value: 'newest',      reverse: false },
];

function SortDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const current = SORT_OPTIONS.find(o => o.value === value) ?? SORT_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} onMouseDown={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '10px 20px', minHeight: 44,
          border: `1px solid ${open ? DARK : 'rgba(42,31,20,0.18)'}`,
          background: open ? DARK : '#fff',
          fontFamily: 'DM Sans, sans-serif', fontSize: 10,
          letterSpacing: '0.13em', textTransform: 'uppercase',
          fontWeight: 600, color: open ? '#fff' : 'rgba(42,31,20,0.6)',
          cursor: 'pointer', transition: 'all 0.18s',
          boxShadow: open ? '0 2px 12px rgba(42,31,20,0.12)' : 'none',
        }}
      >
        <SlidersHorizontal size={13} color={open ? 'rgba(255,255,255,0.7)' : GOLD} />
        {current.label}
        <ChevronDown size={11} color={open ? 'rgba(255,255,255,0.7)' : 'rgba(42,31,20,0.35)'} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 30,
          background: '#fff', border: `1px solid ${LINE}`,
          minWidth: 200, boxShadow: '0 12px 40px rgba(42,31,20,0.1)',
        }}>
          {SORT_OPTIONS.map(o => {
            const active = value === o.value;
            return (
              <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }} style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                background: active ? 'rgba(210,60,120,0.07)' : 'transparent',
                border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                color: active ? DARK : 'rgba(42,31,20,0.7)', fontWeight: active ? 600 : 400,
                borderLeft: active ? `2px solid ${GOLD}` : '2px solid transparent',
              }}>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────── */
function normalizeTag(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-');
}

// Tags are always empty in the Shopify response — all filtering uses collection handles.
function applyFilters(products: ShopifyProduct[], filters: FilterState): ShopifyProduct[] {
  return products.filter(p => {
    const price = parseFloat(p.priceRange.minVariantPrice.amount);
    const cols  = new Set((p.collections?.nodes ?? []).map(c => c.handle));

    if (filters.okazje.length > 0) {
      const ok = filters.okazje.some(o => {
        const h = OKAZJE.find(x => x.value === o)?.handle;
        return h ? cols.has(h) : false;
      });
      if (!ok) return false;
    }

    if (filters.kwiaty.length > 0) {
      const ok = filters.kwiaty.some(k => {
        const h = KWIATY.find(x => x.value === k)?.handle;
        return h ? cols.has(h) : false;
      });
      if (!ok) return false;
    }

    if (price < filters.priceRange[0] || price > filters.priceRange[1]) return false;
    return true;
  });
}

function SkeletonGrid() {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
      gap: 24,
    }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{
          borderRadius: 12, overflow: 'hidden',
          background: 'linear-gradient(90deg, #F0EAE0 25%, #F8F4EE 50%, #F0EAE0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          aspectRatio: '3/4',
        }} />
      ))}
    </div>
  );
}


/* ─── Main page ───────────────────────────────────────── */
export default function KwiatyPage() {
  useSeo(PAGE_SEO.kwiaty);
  const BASE_URL = import.meta.env.BASE_URL;

  const [allProducts, setAllProducts] = useState<ShopifyProduct[]>([]);
  const [compositionProducts, setCompositionProducts] = useState<ShopifyProduct[] | null>(null);
  const [addonProductIds, setAddonProductIds] = useState<Set<string>>(() => new Set());
  const [loading,  setLoading]  = useState(true);
  const [compositionLoading, setCompositionLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [filters,  setFilters]  = useState<FilterState>(EMPTY_FILTERS);
  const [sort,     setSort]     = useState('default');
  const pageBusy = loading || compositionLoading;
  usePrerenderReady(!loading && !compositionLoading);

  /* Full flower catalogue. Addon membership is resolved from the exact
   * collection instead of the truncated product.collections connection. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    Promise.all([
      fetchAllProducts(),
      fetchAllCollectionProducts('wazony'),
    ])
      .then(([catalog, addons]) => {
        if (cancelled) return;
        const addonIds = new Set(addons.map(product => product.id));
        const normalised = catalog
          .filter(product => !addonIds.has(product.id))
          .map(product => ({ ...product, tags: product.tags ?? [] }));
        setAddonProductIds(addonIds);
        setAllProducts(normalised);
        if (normalised.length) {
          const max = Math.ceil(
            Math.max(...normalised.map(pr => parseFloat(pr.priceRange.minVariantPrice.amount))) / 50
          ) * 50;
          setMaxPrice(max);
          setFilters(f => ({ ...f, priceRange: [0, max] }));
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError('Nie udało się pobrać katalogu kwiatów.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const selectedCompositionHandles = useMemo(
    () => filters.kompozycje
      .map(value => KWIATY_KOMPOZYCJE.find(option => option.value === value)?.handle)
      .filter((handle): handle is string => Boolean(handle)),
    [filters.kompozycje],
  );
  const selectedCompositionKey = selectedCompositionHandles.join('|');

  /* Composition membership comes directly from Shopify collections. Multiple
   * selected types use union semantics and are deduplicated by product ID. */
  useEffect(() => {
    let cancelled = false;
    if (selectedCompositionHandles.length === 0) {
      setCompositionProducts(null);
      setCompositionLoading(false);
      setLoadError(null);
      return;
    }

    setCompositionLoading(true);
    setCompositionProducts([]);
    setLoadError(null);
    Promise.all(selectedCompositionHandles.map(handle => fetchAllCollectionProducts(handle)))
      .then(groups => {
        if (cancelled) return;
        const productsById = new Map<string, ShopifyProduct>();
        groups.flat().forEach(product => {
          if (!addonProductIds.has(product.id)) {
            productsById.set(product.id, { ...product, tags: product.tags ?? [] });
          }
        });
        setCompositionProducts([...productsById.values()]);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Nie udało się pobrać wybranej kolekcji.');
      })
      .finally(() => {
        if (!cancelled) setCompositionLoading(false);
      });

    return () => { cancelled = true; };
  }, [selectedCompositionKey, addonProductIds, selectedCompositionHandles]);

  const products = useMemo(
    () => (compositionProducts ?? allProducts)
      .filter(product => !addonProductIds.has(product.id)),
    [allProducts, addonProductIds, compositionProducts],
  );

  /* Available occasion and flower filters remain based on the complete
   * unfiltered catalogue, so choosing a composition does not hide options. */
  const availableOkazje = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return OKAZJE.filter(o => o.handle && handles.has(o.handle));
  }, [allProducts]);

  const availableKwiaty = useMemo(() => {
    const handles = new Set(allProducts.flatMap(p => (p.collections?.nodes ?? []).map(c => c.handle)));
    return KWIATY.filter(k => k.handle && handles.has(k.handle));
  }, [allProducts]);

  /* filter + sort */
  const filtered = useMemo(() => applyFilters(products, filters), [products, filters]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    if (sort === 'price-asc')  copy.sort((a, b) => parseFloat(a.priceRange.minVariantPrice.amount) - parseFloat(b.priceRange.minVariantPrice.amount));
    if (sort === 'price-desc') copy.sort((a, b) => parseFloat(b.priceRange.minVariantPrice.amount) - parseFloat(a.priceRange.minVariantPrice.amount));
    if (sort === 'newest')     copy.sort((a, b) => b.id.localeCompare(a.id));
    return copy;
  }, [filtered, sort]);

  const activeFilters = hasActiveFilters(filters, maxPrice);
  const clearAll = () => setFilters({ ...EMPTY_FILTERS, priceRange: [0, maxPrice] });

  const breadcrumbs = [{ label: 'Kwiaty' }];

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <PageBreadcrumb crumbs={breadcrumbs} />

      {/* ── Page header ── */}
      <section style={{ background: '#fff', padding: 'clamp(48px,6vw,72px) 0 clamp(32px,4vw,48px)' }}>
        <div className="page-container">
          <div>
            <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem,4.5vw,3.8rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.06 }}><CmsEditorialText id="editorial.KwiatyPage.h1.1" value={"Kwiaty i bukiety "} section="Wstęp i opisy" label="Nagłówek 1" /><span style={{ color: PINK }}><CmsEditorialText id="category.accent" value="według gatunku" section="Wstęp i opisy" label="Wyróżniona część nagłówka" /></span>
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, lineHeight: 1.7, color: MUTED, maxWidth: 500, margin: '0 0 12px', fontStyle: 'italic' }}><CmsEditorialText id="editorial.KwiatyPage.p.1" value={"Szukasz konkretnego gatunku? Przeglądaj kwiaty i kompozycje według składu, a przed zakupem sprawdź opis oraz dostępność wybranego produktu."} section="Wstęp i opisy" label="Akapit 1" multiline /></p>
            {!pageBusy && (
              <p style={{ fontFamily: 'DM Sans', fontSize: 13, color: MUTED, margin: 0 }}>
                {products.length} {products.length === 1 ? 'produkt' : products.length < 5 ? 'produkty' : 'produktów'}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ── White section: filter bar + products ── */}
      <div className="page-container" style={{ background: '#fff' }}>

      {/* ── FILTER BAR ───────────────────────────────── */}
      {!pageBusy && products.length > 0 && (
        <div style={{
          background: '#ffffff',
          borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`,
        }}>
          <div style={{ padding: '14px 0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CollectionFilters
                  filters={filters}
                  onChange={setFilters}
                  maxPrice={maxPrice}
                  sortValue={sort}
                  onSortChange={setSort}
                  sortOptions={SORT_OPTIONS}
                  visibleSections={[...KWIATY_VISIBLE_SECTIONS]}
                  kompozycjeOptions={KWIATY_KOMPOZYCJE}
                  kompozycjeLabel="Rodzaj kompozycji"
                  okazjeOptions={availableOkazje.length > 0 ? availableOkazje : undefined}
                  kwiatyOptions={availableKwiaty.length > 0 ? availableKwiaty : undefined}
                />
              </div>
              <SortDropdown value={sort} onChange={setSort} />
            </div>
          </div>
        </div>
      )}

      {/* ── PRODUCTS SECTION ─────────────────────────── */}
      <section style={{ padding: 'clamp(40px,5vw,64px) 0 clamp(64px,8vw,120px)' }}>

        {/* Grid */}
        {pageBusy && <SkeletonGrid />}

        {!pageBusy && loadError && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: DARK, marginBottom: 8 }}>
              Nie udało się załadować produktów
            </p>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED }}>{"Odśwież stronę i spróbuj ponownie."}</p>
          </div>
        )}

        {!pageBusy && !loadError && sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Flower2 size={48} strokeWidth={1} style={{ color: SEP, marginBottom: 16 }} />
            <p style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 28, color: DARK, marginBottom: 8 }}>
              Brak produktów
            </p>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, marginBottom: 20 }}>
              Spróbuj zmienić filtry lub przeglądaj całą kolekcję
            </p>
            <button
              onClick={clearAll}
              style={{
                background: 'none', border: `1px solid ${GOLD}`, color: GOLD,
                fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em',
                textTransform: 'uppercase', padding: '10px 24px', borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Wyczyść filtry
            </button>
          </div>
        )}

        {!pageBusy && !loadError && sorted.length > 0 && (
          <div className="kwiaty-grid" style={{ display: 'grid', gap: 24 }}>
            {sorted.map((product, i) => (
              <CollectionProductCard
                key={product.handle}
                product={product}
                price={productDisplayPrice(product.priceRange)}
                index={i}
                collectionCtx={{ label: 'Kwiaty', href: collectionUrl('kwiaty') }}
                centerInfo
              />
            ))}
          </div>
        )}

      </section>

      </div>{/* end white section */}
      {!pageBusy && <CategorySeoShowcase config={KWIATY_SEO_CONFIG} />}
      <Footer />

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .kwiaty-grid { grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 768px)  { .kwiaty-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 768px) {
          .hero-kwiaty {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
